import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Box } from '@mui/joy';
import { CssVarsProvider } from '@mui/joy/styles';
import { observer } from 'mobx-react-lite';

import { UnitConversions, roundFloatToInt } from '../../general-utilities/numbers-util';
import { millisecondsToHHMMSS, secondsToHHMMSS } from '../../general-utilities/time-util';
import { serverBaseWebSocketUrl } from '../../general-utilities/urlUtils';
import { useVoiceChat } from '../../hooks/useVoiceChat';
import { CrewService } from '../../services/CrewService';
import { SharedSessionParticipant, SharedSessionResponse } from '../../services/types/api.generated';
import sessionStore from '../../stores/SessionStore';
import crewTheme from '../../themes/crew';
import { MapType } from '../Mapbox/mapTypes';
import { FitMode } from '../VideoScaler';
import CrewInstrumentSettingsModal from './CrewInstrumentSettingsModal';
import CrewToast from './CrewToast';
import CrewToastContainer from './CrewToastContainer';
import CrewViewContent from './CrewViewContent';
import CrewViewDock from './CrewViewDock';
import CrewViewHeader from './CrewViewHeader';
import { useCrewTelemetry } from './hooks/useCrewTelemetry';
import { useCrewViewOrientation } from './hooks/useCrewViewOrientation';
import { useCrewViewState } from './hooks/useCrewViewState';
import { useCrewViewWebSockets } from './hooks/useCrewViewWebSockets';
import { useTelemetryBackfill } from './hooks/useTelemetryBackfill';
import InstrumentsPanel from './InstrumentsPanel';
import LoadingOverlay from './LoadingOverlay';
import PortraitPopup from './PortraitPopup';
import { AgentStatus } from './types';

export type CrewViewProps = {
  participantId: string;
  sessionId: string;
  deviceUuid: string;
  onSessionEnded?: () => void;
};

/**
 * Convert feet to Mapbox zoom level
 * Formula: zoom = log2((earthCircumference * cos(latitude)) / (256 * metersPerPixel))
 * @param feet - Height in feet to display
 * @param latitude - Latitude for accurate calculation (default: 37°)
 * @param viewportHeight - Viewport height in pixels (default: 800)
 * @returns Mapbox zoom level
 */
const feetToZoomLevel = (feet: number, latitude = 37, viewportHeight = 800): number => {
  const feetToMeters = 0.3048;
  const meters = feet * feetToMeters;
  const metersPerPixel = meters / viewportHeight;

  const earthCircumference = 40075017; // meters at equator
  const latitudeRad = (latitude * Math.PI) / 180;

  const zoom = Math.log2((earthCircumference * Math.cos(latitudeRad)) / (256 * metersPerPixel));
  return zoom;
};

const CREW_VIEW_ZOOM_FT = 5000;
const CREW_VIEW_INITIAL_ZOOM = feetToZoomLevel(CREW_VIEW_ZOOM_FT);

const CrewView: React.FC<CrewViewProps> = observer(({ participantId, sessionId, deviceUuid, onSessionEnded }) => {
  const { t } = useTranslation();
  const [isFullscreenVideo, setIsFullscreenVideo] = useState(true);
  const [fitMode, setFitMode] = useState<FitMode>('fitWidth');
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('idle');

  // Voice chat - single connection shared between components
  const voiceChat = useVoiceChat({
    config: {
      sessionId,
      participantId,
      deviceUuid
    },
    autoConnect: true
  });

  // Extract state management
  const {
    isPending,
    setIsPending,
    participants,
    setParticipants,
    sessionData,
    setSessionData,
    markerImages,
    setMarkerImages,
    instrumentSettings,
    setInstrumentSettings,
    isInstrumentsVisible,
    toggleInstrumentsShowState,
    isInstrumentSettingsModalOpen,
    setIsInstrumentSettingsModalOpen,
    activeVideoServerUrl,
    setActiveVideoServerUrl,
    pingVideoUrl,
    setPingVideoUrl,
    agl,
    updateAgl,
    liveLine,
    setLiveLine,
    handleOpenInstrumentSettings
  } = useCrewViewState(deviceUuid, isFullscreenVideo);

  // Extract orientation detection
  const { showPortraitPopup } = useCrewViewOrientation();

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapType | null>(null);

  const {
    latestCrewTelemetry,
    handleTelemetryMessage,
    agl: telemetryAgl
  } = useCrewTelemetry({
    liveLine,
    setLiveLine,
    mapRef,
    markerImages,
    setMarkerImages,
    onAglUpdate: updateAgl
  });

  // Handle telemetry backfilling
  const handleLinesUpdate = (newLines: [number, number][]) => {
    if (newLines.length === 0) return;

    setLiveLine((prev) => {
      // Deduplicate: filter out points that already exist
      return {
        ...prev,
        lines: [
          ...newLines,
          ...prev.lines.filter((line) => !newLines.some((newLine) => newLine[0] === line[0] && newLine[1] === line[1]))
        ]
      };
    });
  };

  const telemetryUrlBuilder = (pId: string) => {
    return `${serverBaseWebSocketUrl}/ws/shared-session/${sessionId}/participant/${pId}/device/${deviceUuid}/telemetry`;
  };

  const pingUrlBuilder = (pId: string) => {
    return `${serverBaseWebSocketUrl}/ws/shared-session/${sessionId}/participant/${pId}/device/${deviceUuid}/ping`;
  };

  const handleEventMessage = useCallback(
    (event: string, extra: Record<string, unknown>) => {
      if (event === 'agent_joined') {
        setAgentStatus('active');
      } else if (event === 'agent_left') {
        setAgentStatus('idle');
      }
    },
    []
  );

  const handlePongMessage = (data: SharedSessionResponse & { participants: SharedSessionParticipant[] }) => {
    if (data) {
      setSessionData(data);
      const normalizedVideoUrl = data.video_url?.trim().replace(/\/$/, '');
      const whepUrl = normalizedVideoUrl ? `${normalizedVideoUrl}/whep` : '';
      setPingVideoUrl(whepUrl);
    }
    if (data && data.participants) {
      setParticipants(data.participants);
    }
  };

  // Extract WebSocket logic
  const { connect, connectionState } = useCrewViewWebSockets({
    sessionId,
    participantId,
    deviceUuid,
    telemetryUrlBuilder,
    pingUrlBuilder,
    onTelemetryMessage: handleTelemetryMessage,
    onPongMessage: handlePongMessage,
    onEventMessage: handleEventMessage,
    onSessionEnded,
    setSessionData,
    setParticipants,
    setPingVideoUrl
  });

  // Handle telemetry backfilling
  useTelemetryBackfill({
    sessionId,
    participantId,
    deviceUuid,
    connectionState,
    latestCrewTelemetry,
    onLinesUpdate: handleLinesUpdate
  });

  const changeView = (): void => {
    setIsFullscreenVideo(!isFullscreenVideo);
  };

  useEffect(() => {
    if (sessionId) {
      setIsPending(true);

      const initializeSession = async () => {
        try {
          // Connect WebSocket first to start receiving live telemetry immediately
          await connect();

          // Load participants
          CrewService.getParticipants(sessionId).then((res) => {
            if (res) {
              setParticipants(res);
            }
          });

          const toastMessage = t('crew_view.toast.viewing_session', { sessionName: sessionId });
          toast(CrewToast, {
            data: { message: toastMessage }
          });
        } catch (error) {
          console.error('Failed to initialize session:', error);
        } finally {
          setIsPending(false);
        }
      };

      initializeSession();
    }
  }, [sessionId, participantId, deviceUuid, connect, t, setParticipants, setIsPending]);

  useEffect(() => {
    document.documentElement.setAttribute('data-joy-color-scheme', 'dark');
    return () => {
      document.documentElement.setAttribute(
        'data-joy-color-scheme',
        sessionStore.user?.color_scheme_preference || 'default'
      );
    };
  }, []);

  const onlineParticipants = participants.filter((participant) => participant.is_online);

  const dockProps = {
    agl,
    asl: UnitConversions.mToF(latestCrewTelemetry?.position?.absolute_altitude_m ?? 0).toFixed(1),
    airSpeed: UnitConversions.mpsToMph(roundFloatToInt(latestCrewTelemetry?.velocity?.airspeed_m_s)).toFixed(1),
    groundSpeed: UnitConversions.mpsToMph(latestCrewTelemetry?.velocity?.ground_speed_m_s ?? 0).toFixed(1),
    time: millisecondsToHHMMSS(latestCrewTelemetry?.flight_info?.duration_since_arming_ms ?? 0),
    remainingTime:
      latestCrewTelemetry?.battery?.time_remaining_s && latestCrewTelemetry?.battery?.time_remaining_s > 0
        ? secondsToHHMMSS(latestCrewTelemetry?.battery?.time_remaining_s ?? 0)
        : '--.--',
    homeDistance: UnitConversions.mToF(latestCrewTelemetry?.home_position?.distance_to_home_m ?? 0).toFixed(1)
  };

  const videoUrl = (() => {
    if (!deviceUuid) {
      return '';
    }

    // Use WHEP URL directly if available
    if (pingVideoUrl !== null && pingVideoUrl !== undefined) {
      return pingVideoUrl;
    }

    return `${activeVideoServerUrl}/${deviceUuid}/whep`;
  })();

  return (
    <CssVarsProvider theme={crewTheme} disableTransitionOnChange>
      <Box className='flex flex-col w-full h-full relative' sx={{ bgcolor: 'background.level1' }}>
        <CrewViewHeader
          sessionName={sessionId}
          link={`${window.location.origin}/crew/${sessionId}/device/${deviceUuid}`}
          fitMode={fitMode}
          onFitModeChange={setFitMode}
        />
        <LoadingOverlay isPending={isPending} />

        <CrewViewContent
          containerRef={containerRef}
          isFullscreenVideo={isFullscreenVideo}
          liveLine={liveLine}
          markerImages={markerImages}
          mapRef={mapRef}
          videoUrl={videoUrl}
          initialZoom={CREW_VIEW_INITIAL_ZOOM}
          onChangeView={changeView}
          sessionId={sessionId}
          participantId={participantId}
          deviceUuid={deviceUuid}
          onlineParticipants={onlineParticipants}
          viewerCount={sessionData?.viewer_count ?? onlineParticipants.length}
          microphoneEnabled={voiceChat.microphoneEnabled}
          isVoiceChatConnected={voiceChat.isConnected}
          isVoiceChatConnecting={voiceChat.isConnecting}
          onToggleMicrophone={async () => {
            await voiceChat.setMicrophoneEnabled(!voiceChat.microphoneEnabled);
          }}
          onSetMicrophone={async (enabled: boolean) => {
            await voiceChat.setMicrophoneEnabled(enabled);
          }}
          onToggleVoiceChat={async () => {
            if (voiceChat.isConnected) {
              await voiceChat.disconnect();
            } else {
              await voiceChat.connect();
            }
          }}
          voiceChatParticipants={voiceChat.participants}
          activeSpeakers={voiceChat.activeSpeakers}
          fitMode={fitMode}
          onFitModeChange={setFitMode}
          agentStatus={agentStatus}
          setAgentStatus={setAgentStatus}
        />
      </Box>

      <InstrumentsPanel
        isInstrumentsVisible={isInstrumentsVisible}
        instrumentSettings={instrumentSettings}
        latestCrewTelemetry={latestCrewTelemetry}
        onToggleVisibility={toggleInstrumentsShowState}
        onOpenSettings={handleOpenInstrumentSettings}
      />

      <CrewViewDock {...dockProps} />

      <CrewInstrumentSettingsModal
        open={isInstrumentSettingsModalOpen}
        onClose={() => setIsInstrumentSettingsModalOpen(false)}
        instrumentSettings={instrumentSettings}
        setInstrumentSettings={setInstrumentSettings}
      />
      <CrewToastContainer />

      <PortraitPopup show={showPortraitPopup} />
    </CssVarsProvider>
  );
});

export default CrewView;
